import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../api/client';
import { queryClient } from '../api/queryClient';
import { User } from '../types';

interface PendingStoreSelection {
  user: User;
  token: string;
  refreshToken: string;
  stores: any[];
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<any>;
  loginWithGoogle: (credential: string) => Promise<any>;
  registerWithGoogle: (data: GoogleRegisterData) => Promise<void>;
  verify2FA: (email: string, code: string) => Promise<User>;
  register: (data: RegisterData) => Promise<any>;
  logout: () => void;
  setUser: (user: User, token: string, refreshToken?: string) => void;
  initializeAuth: () => void;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email?: string) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<User>;
  changePassword: (data: any) => Promise<void>;
  resend2FA: (email: string) => Promise<void>;
  activeStoreId: string | null;
  setActiveStore: (storeId: string) => void;
  pendingStoreSelection: PendingStoreSelection | null;
  finalizeStoreSelection: (storeId: string) => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: 'CUSTOMER' | 'MERCHANT';
  // Organisation / Store fields for merchants
  organisationName?: string;
  storeName?: string;
  storeSubdomain?: string;
  storePhone?: string;
  // Business address (for Google Merchant Center compliance)
  streetAddress?: string;
  city?: string;
  county?: string;
  postalCode?: string;
  country?: string;
}

interface GoogleRegisterData extends Omit<RegisterData, 'email' | 'password' | 'firstName' | 'lastName' | 'role'> {
  credential: string;
}

const getAccessibleStores = (user: any): any[] => {
  // `user.stores` is produced by the backend's authorization-aware formatter.
  // Never expand membership.account.stores here: a store-scoped membership does
  // not grant access to sibling stores in the same organisation.
  const candidates = Array.isArray(user?.stores)
    ? user.stores
    : Array.isArray(user?.ownedStores)
      ? user.ownedStores
      : [];

  return Array.from(
    new Map(candidates.filter((store: any) => store?.id).map((store: any) => [store.id, store])).values()
  );
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      activeStoreId: null,
      pendingStoreSelection: null,

      initializeAuth: () => {
        const state = get();
        if (state.token) {
          apiClient.setAccessToken(state.token);
          localStorage.setItem('accessToken', state.token);
        }
        if (state.refreshToken) {
          apiClient.setRefreshToken(state.refreshToken);
          localStorage.setItem('refreshToken', state.refreshToken);
        }
        if (state.activeStoreId) {
          apiClient.setActiveStoreId(state.activeStoreId);
          localStorage.setItem('activeStoreId', state.activeStoreId);
        }
      },

      login: async (email: string, password: string) => {
        try {
          queryClient.clear();
          apiClient.clearTokens();
          set({ user: null, token: null, refreshToken: null, isAuthenticated: false, activeStoreId: null, pendingStoreSelection: null });
          const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
          const response = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });

          if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            if (response.status === 429) {
              const retryAfter = Math.max(
                1,
                Number(response.headers.get('Retry-After') || errorBody.retryAfter || 900)
              );
              const minutes = Math.ceil(retryAfter / 60);
              const rateLimitError: any = new Error(
                `Too many sign-in attempts. Please wait about ${minutes} minute${minutes === 1 ? '' : 's'} before trying again.`
              );
              rateLimitError.statusCode = 429;
              rateLimitError.retryAfter = retryAfter;
              throw rateLimitError;
            }
            const loginError: any = new Error(
              errorBody.error?.message || errorBody.message || 'We could not sign you in. Check your details and try again.'
            );
            loginError.statusCode = response.status;
            throw loginError;
          }

          const result = await response.json();

          // Handle 2FA requirement
          if (result.data.requires2FA) {
            return result.data;
          }

          // Handle Email Verification requirement
          if (result.data.requiresVerification) {
            return result.data;
          }

          const { user, accessToken, refreshToken } = result.data;

          const uniqueStores = getAccessibleStores(user);

          if (uniqueStores.length > 1) {
            // IMPORTANT: Push the NEW tokens into the apiClient immediately so
            // the old (expired) tokens from the previous session are evicted.
            // Without this, ensureValidToken() runs on background requests,
            // finds the stale localStorage tokens, tries to refresh them,
            // gets a 401, and forces a logout before the user even picks a store.
            apiClient.setAccessToken(accessToken);
            apiClient.setRefreshToken(refreshToken);
            apiClient.setActiveStoreId(null);

            set({
              user: null,
              token: null,
              refreshToken: null,
              isAuthenticated: false,
              activeStoreId: null,
              pendingStoreSelection: {
                user,
                token: accessToken,
                refreshToken,
                stores: uniqueStores,
              },
            });
            return { requiresStoreSelection: true };
          }


          // Set tokens in API client for authenticated requests (if 1 or 0 stores)
          apiClient.setAccessToken(accessToken);
          apiClient.setRefreshToken(refreshToken);

          const defaultStoreId = uniqueStores.length === 1 ? uniqueStores[0].id : null;
          if (defaultStoreId) {
            apiClient.setActiveStoreId(defaultStoreId);
          }

          set({
            user,
            token: accessToken,
            refreshToken: refreshToken,
            isAuthenticated: true,
            activeStoreId: defaultStoreId,
          });

          return user;
        } catch (error) {
          throw error;
        }
      },

      loginWithGoogle: async (credential: string) => {
        queryClient.clear();
        apiClient.clearTokens();
        set({ user: null, token: null, refreshToken: null, isAuthenticated: false, activeStoreId: null, pendingStoreSelection: null });
        const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${API_URL}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential }),
        });
        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          const googleError: any = new Error(
            errorBody.error?.message || errorBody.message || 'Google sign-in failed. Please try again.'
          );
          googleError.statusCode = response.status;
          throw googleError;
        }

        const result = await response.json();
        const { user, accessToken, refreshToken } = result.data;
        const uniqueStores = getAccessibleStores(user);

        apiClient.setAccessToken(accessToken);
        apiClient.setRefreshToken(refreshToken);
        if (uniqueStores.length > 1) {
          apiClient.setActiveStoreId(null);
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            activeStoreId: null,
            pendingStoreSelection: { user, token: accessToken, refreshToken, stores: uniqueStores },
          });
          return { requiresStoreSelection: true };
        }

        const defaultStoreId = uniqueStores.length === 1 ? (uniqueStores[0] as any).id : null;
        if (defaultStoreId) apiClient.setActiveStoreId(defaultStoreId);
        set({
          user,
          token: accessToken,
          refreshToken,
          isAuthenticated: true,
          activeStoreId: defaultStoreId,
          pendingStoreSelection: null,
        });
        return user;
      },

      registerWithGoogle: async (data: GoogleRegisterData) => {
        queryClient.clear();
        apiClient.clearTokens();
        set({ user: null, token: null, refreshToken: null, isAuthenticated: false, activeStoreId: null, pendingStoreSelection: null });
        const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${API_URL}/auth/google/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody.error?.message || errorBody.message || 'Google registration failed.');
        }
        const result = await response.json();
        const { user, store, accessToken, refreshToken } = result.data;
        apiClient.setAccessToken(accessToken);
        apiClient.setRefreshToken(refreshToken);
        apiClient.setActiveStoreId(store.id);
        set({
          user,
          token: accessToken,
          refreshToken,
          isAuthenticated: true,
          activeStoreId: store.id,
          pendingStoreSelection: null,
        });
      },

      verify2FA: async (email: string, code: string) => {
        try {
          const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
          const response = await fetch(`${API_URL}/auth/verify-2fa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error?.message || error.message || 'Verification failed');
          }

          const result = await response.json();
          const { user, accessToken, refreshToken } = result.data;

          const uniqueStores = getAccessibleStores(user);

          if (uniqueStores.length > 1) {
            set({
              pendingStoreSelection: {
                user,
                token: accessToken,
                refreshToken,
                stores: uniqueStores,
              },
            });
            return { requiresStoreSelection: true };
          }

          // Set tokens in API client for authenticated requests (if 1 or 0 stores)
          apiClient.setAccessToken(accessToken);
          apiClient.setRefreshToken(refreshToken);

          const defaultStoreId = uniqueStores.length === 1 ? uniqueStores[0].id : null;
          if (defaultStoreId) {
            apiClient.setActiveStoreId(defaultStoreId);
          }

          set({
            user,
            token: accessToken,
            refreshToken: refreshToken,
            isAuthenticated: true,
            activeStoreId: defaultStoreId,
          });

          return user;
        } catch (error) {
          throw error;
        }
      },

      resend2FA: async (email: string) => {
        try {
          const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
          const response = await fetch(`${API_URL}/auth/resend-2fa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error?.message || error.message || 'Failed to resend code');
          }
        } catch (error) {
          throw error;
        }
      },



      register: async (registerData: RegisterData) => {
        try {
          const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
          const response = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(registerData),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error?.message || error.message || 'Registration failed');
          }

          const result = await response.json();

          if (result.data?.requiresVerification) {
            return result.data;
          }

          const { user, accessToken, refreshToken } = result.data;

          if (accessToken) {
            // Set tokens in API client for authenticated requests
            apiClient.setAccessToken(accessToken);
            if (refreshToken) apiClient.setRefreshToken(refreshToken);

            const defaultStoreId = user?.storeId || getAccessibleStores(user)[0]?.id || null;
            if (defaultStoreId) {
              apiClient.setActiveStoreId(defaultStoreId);
            }

            set({
              user,
              token: accessToken,
              refreshToken: refreshToken || null,
              isAuthenticated: true,
              activeStoreId: defaultStoreId,
            });
          }

          return result.data;
        } catch (error) {
          throw error;
        }
      },

      finalizeStoreSelection: (storeId: string) => {
        const state = get();
        if (!state.pendingStoreSelection) return;
        
        const { user, token, refreshToken } = state.pendingStoreSelection;
        
        apiClient.setAccessToken(token);
        if (refreshToken) apiClient.setRefreshToken(refreshToken);
        apiClient.setActiveStoreId(storeId);
        
        set({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          activeStoreId: storeId,
          pendingStoreSelection: null,
        });
      },

      logout: () => {
        // Clear token from API client
        apiClient.clearTokens();

        // Clear all local storage except persist keys we want to keep
        const keysToKeep = ['theme', 'language'];
        const allKeys = Object.keys(localStorage);
        allKeys.forEach(key => {
          if (!keysToKeep.includes(key)) {
            localStorage.removeItem(key);
          }
        });

        // Clear session storage
        sessionStorage.clear();

        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          activeStoreId: null,
        });

        // Reload the page to clear React Query cache and all state
        window.location.href = '/login';
      },

      verifyEmail: async (token: string) => {
        try {
          const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
          const response = await fetch(`${API_URL}/auth/verify-email?token=${token}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error?.message || error.message || 'Verification failed');
          }

          const currentUser = get().user;
          if (currentUser) {
            set({ user: { ...currentUser, emailVerified: true } });
          }
        } catch (error) {
          throw error;
        }
      },

      resendVerification: async (email?: string) => {
        try {
          const targetEmail = email || get().user?.email;
          const response = await apiClient.post('/auth/resend-verification', { email: targetEmail });
          return (response as any).data;
        } catch (error: any) {
          throw new Error(error.response?.data?.error?.message || error.message || 'Failed to resend verification');
        }
      },

      setUser: (user: User, token: string, refreshToken?: string) => {
        const state = get();
        // Set token in API client when setting user
        apiClient.setAccessToken(token);
        if (refreshToken) {
          apiClient.setRefreshToken(refreshToken);
        }
        
        // Only default the activeStoreId if one isn't already set or rehydrated
        if (!state.activeStoreId) {
          const defaultStoreId = user.storeId || getAccessibleStores(user)[0]?.id || null;
          if (defaultStoreId) {
            apiClient.setActiveStoreId(defaultStoreId);
            set({ activeStoreId: defaultStoreId });
          }
        }

        set({
          user,
          token,
          refreshToken: refreshToken || null,
          isAuthenticated: true,
        });
      },

      setActiveStore: (storeId: string) => {
        apiClient.setActiveStoreId(storeId);
        set({ activeStoreId: storeId });
      },

      updateProfile: async (data: Partial<User>) => {
        try {
          const response = await apiClient.patch('/auth/me', data);
          const updatedUser = (response as any).data;
          set({ user: updatedUser });
          return updatedUser;
        } catch (error: any) {
          throw new Error(error.response?.data?.error?.message || error.message || 'Failed to update profile');
        }
      },

      changePassword: async (data: any) => {
        try {
          await apiClient.post('/auth/change-password', data);
        } catch (error: any) {
          throw new Error(error.response?.data?.error?.message || error.message || 'Failed to change password');
        }
      },
    }),
    {
      name: 'auth-storage',
      onRehydrateStorage: () => (state) => {
        // IMPORTANT: This handler runs before anytime the app boots or reloads.
        // Any state variables that are required by the ApiClient interceptors, 
        // the SSE hooks, or any module-level initialization must be explicitly 
        // synced here. Zustand's normal state updates are too slow for the 
        // initial burst of network requests after a page reload.

        // Prefer top-level tokens (fully authenticated). Fall back to
        // pendingStoreSelection tokens (mid store-selection flow, e.g. after
        // a page refresh on /select-store).
        const accessToken = state?.token || state?.pendingStoreSelection?.token;
        const refreshToken = state?.refreshToken || state?.pendingStoreSelection?.refreshToken;

        if (accessToken) {
          apiClient.setAccessToken(accessToken);
          localStorage.setItem('accessToken', accessToken);
        }
        if (refreshToken) {
          apiClient.setRefreshToken(refreshToken);
          localStorage.setItem('refreshToken', refreshToken);
        }
        if (state?.activeStoreId) {
          apiClient.setActiveStoreId(state.activeStoreId);
          localStorage.setItem('activeStoreId', state.activeStoreId);
        }
      },

    }
  )
);
